// Shared-world client (Phase 4). Mirrors the server market into the local engine, routes market
// actions to the server, applies its fills as recorded commands, and keeps the clock in sync.
import { signal } from "@preact/signals";
import type { Command, CommandResult, Engine } from "@degen/sim/engine";
import type { Token } from "@degen/sim/market/token";
import { gasFee, outOfGasMessage } from "@degen/sim/gas";
import { TUNABLES } from "@degen/shared/config/tunables";
import type { ClientMsg, GroupInfo, MirrorToken, ServerMsg, TokenDelta, WorldPost } from "@degen/shared/world";
import { clientEnv } from "./env";
import { deviceId } from "./identity";
import { authToken } from "./session";
import { showToast, pushSnapshot } from "../ui/bridge";
import { wusd, tokens as fmtTokens } from "../ui/format";

export const worldStatus = signal<"offline" | "connecting" | "online">("offline");
export const worldPosts = signal<WorldPost[]>([]);
export const worldGroups = signal<GroupInfo[]>([]);
export const worldInfo = signal<{ id: string; handle: string } | null>(null);
const muted = new Set<string>();

let ws: WebSocket | null = null;
let engine: Engine | null = null;
let me = "";
let req = 0;
let backoff = 1000;
let sync = { tick: 0, at: 0, tps: 0 };
const pending = new Map<number, (m: ServerMsg) => void>();

/** Ticks the local engine should be at right now (server clock + elapsed). */
export function targetTick(): number {
  if (!sync.at) return engine?.state.tick ?? 0;
  return Math.floor(sync.tick + ((performance.now() - sync.at) / 1000) * sync.tps);
}

function setSync(tick: number, tps?: number) {
  sync = { tick, at: performance.now(), tps: tps ?? sync.tps };
}

function toToken(m: MirrorToken): Token {
  const mine = m.creator === `u:${me}`;
  return {
    id: m.id, name: m.name, ticker: m.ticker, creator: mine ? "player" : m.creator, description: m.description + (m.creatorHandle && !mine ? ` · by @${m.creatorHandle}` : ""),
    iconSeed: m.iconSeed, phase: m.phase as Token["phase"], curve: m.curve as Token["curve"], pool: m.pool as Token["pool"], totalSupply: m.totalSupply, devBagPct: m.devBagPct,
    hype: m.hype, candles: m.candles as Token["candles"], holders: m.mine > 0 ? { player: m.mine } : {}, holderCount: m.holderCount, bornTick: m.bornTick, launchPrice: m.launchPrice,
    fate: "normal", devId: mine ? "player" : `dev:${m.id}`, rugged: m.rugged, peakMcap: m.peakMcap, lowSinceTick: m.lowSinceTick, graduatedTick: m.graduatedTick, diedTick: m.diedTick, volumeUsd: m.volumeUsd, trades: [],
  };
}

function applyDelta(t: Token, d: TokenDelta) {
  t.phase = d.phase as Token["phase"];
  t.curve = d.curve as Token["curve"];
  t.pool = d.pool as Token["pool"];
  t.hype = d.hype;
  t.volumeUsd = d.volumeUsd;
  t.peakMcap = d.peakMcap;
  t.rugged = d.rugged;
  t.holderCount = d.holderCount;
  t.graduatedTick = d.graduatedTick;
  t.diedTick = d.diedTick;
  t.lowSinceTick = d.lowSinceTick;
  const c = d.candle as Token["candles"][number] | undefined;
  if (c) {
    const last = t.candles[t.candles.length - 1];
    if (last && last.t === c.t) t.candles[t.candles.length - 1] = c;
    else t.candles.push(c);
    if (t.candles.length > 300) t.candles.splice(0, t.candles.length - 300);
  }
}

function send(m: ClientMsg) {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
}

function request<T extends ServerMsg>(m: ClientMsg & { req: number }): Promise<T> {
  return new Promise((res, rej) => {
    pending.set(m.req, res as (x: ServerMsg) => void);
    send(m);
    setTimeout(() => pending.delete(m.req) && rej(new Error("timeout")), 10_000);
  });
}

function onMessage(msg: ServerMsg) {
  const s = engine!.state;
  switch (msg.t) {
    case "batch":
      for (const m of msg.m) onMessage(m);
      return;
    case "welcome": {
      me = msg.userId;
      worldInfo.value = { id: msg.worldId, handle: msg.handle };
      setSync(msg.tick, msg.ticksPerSec);
      s.shared = { worldId: msg.worldId, userId: msg.userId };
      s.market.tokens = Object.fromEntries(msg.tokens.map((m) => [m.id, toToken({ ...m, mine: msg.mine[m.id] ?? m.mine ?? 0 })]));
      muted.clear();
      for (const id of msg.muted) muted.add(id);
      worldPosts.value = msg.posts.filter((p) => !muted.has(p.userId));
      worldGroups.value = msg.groups;
      worldStatus.value = "online";
      backoff = 1000;
      catchUp();
      pushSnapshot(true);
      return;
    }
    case "market":
      setSync(msg.tick);
      for (const d of msg.tokens) {
        const t = s.market.tokens[d.id];
        if (t) applyDelta(t, d);
      }
      return;
    case "token":
      s.market.tokens[msg.token.id] = toToken(msg.token);
      return;
    case "post":
      if (!muted.has(msg.post.userId)) worldPosts.value = [...worldPosts.value.slice(-199), msg.post];
      return;
    case "hide":
      worldPosts.value = worldPosts.value.filter((p) => p.id !== msg.postId);
      return;
    case "pong":
      setSync(msg.tick);
      return;
    case "kicked":
      showToast(msg.reason);
      return;
    case "error":
      showToast(msg.message);
      return;
    default: {
      const r = "req" in msg && msg.req !== undefined ? pending.get(msg.req) : undefined;
      if (r && "req" in msg && msg.req !== undefined) {
        pending.delete(msg.req);
        r(msg);
      }
      if (msg.t === "groups") worldGroups.value = msg.groups;
    }
  }
}

/** Keep the local clock on the server's: jump long gaps (no 30-day catch-up), simulate short ones. */
function catchUp() {
  const e = engine!;
  const target = targetTick();
  const gap = target - e.state.tick;
  if (gap > TUNABLES.clock.ticksPerDay) {
    e.state.tick = target - TUNABLES.clock.ticksPerDay;
    e.advance(TUNABLES.clock.ticksPerDay);
  }
}

async function connect() {
  if (!clientEnv.apiUrl) return;
  worldStatus.value = "connecting";
  const url = clientEnv.apiUrl.replace(/^http/, "ws") + "/world";
  ws = new WebSocket(url);
  ws.onopen = async () => send({ t: "hello", token: (await authToken()) ?? "", world: engine?.state.shared?.worldId ?? "auto", deviceId });
  ws.onmessage = (ev) => onMessage(JSON.parse(String(ev.data)) as ServerMsg);
  ws.onclose = (ev) => {
    worldStatus.value = "offline";
    if (ev.code === 4003 || ev.code === 4004) {
      showToast(ev.code === 4004 ? "Claim a handle (Settings → Account) to join a shared world." : "Can't join the shared world.");
      return;
    }
    // Reconnect with backoff; the welcome message resyncs everything (§7.4).
    setTimeout(() => void connect(), backoff);
    backoff = Math.min(15_000, backoff * 2);
  };
}

/** Market actions go to the world server; the result comes back as a recorded APPLY_* command. */
function hook(cmd: Command): CommandResult | null {
  const e = engine!;
  const s = e.state;
  const p = s.player;
  if (cmd.type === "BUY_TOKEN" || cmd.type === "SELL_TOKEN") {
    if (worldStatus.value !== "online") return { ok: false, error: "Reconnecting to the world…" };
    const t = s.market.tokens[cmd.tokenId];
    if (!t || t.phase === "dead") return { ok: false, error: "That token is gone." };
    const fee = gasFee({ type: "APPLY_FILL", side: cmd.type === "BUY_TOKEN" ? "buy" : "sell" } as Command);
    if (p.dgn + 1e-9 < fee) return { ok: false, error: outOfGasMessage(fee, p.dgn) };
    const r = ++req;
    if (cmd.type === "BUY_TOKEN") {
      if (!(cmd.usd > 0) || cmd.usd > p.cash + 1e-9) return { ok: false, error: "Not enough cash." };
      void request<Extract<ServerMsg, { t: "fill" }>>({ t: "buy", req: r, tokenId: cmd.tokenId, usd: cmd.usd }).then(onFill, () => showToast("Trade timed out."));
    } else {
      if ((t.holders.player ?? 0) <= 0) return { ok: false, error: "You don't hold this token." };
      void request<Extract<ServerMsg, { t: "fill" }>>({ t: "sell", req: r, tokenId: cmd.tokenId, pct: cmd.pct, tokens: cmd.tokens }).then(onFill, () => showToast("Trade timed out."));
    }
    return { ok: true };
  }
  if (cmd.type === "LAUNCH_COIN") {
    if (worldStatus.value !== "online") return { ok: false, error: "Reconnecting to the world…" };
    const L = TUNABLES.launch;
    if (p.cash < L.coinFeeUsd + (cmd.devBuyUsd || 0)) return { ok: false, error: `You need ${wusd(L.coinFeeUsd)} + your dev buy.` };
    const fee = TUNABLES.gas.fees.LAUNCH_COIN;
    if (p.dgn + 1e-9 < fee) return { ok: false, error: outOfGasMessage(fee, p.dgn) };
    void request<Extract<ServerMsg, { t: "launched" }>>({ t: "launch", req: ++req, name: cmd.name, ticker: cmd.ticker, description: cmd.description, devBuyUsd: cmd.devBuyUsd, iconSeed: cmd.iconSeed }).then((m) => {
      if (!m.ok) return showToast(m.error ?? "Launch failed.");
      engine!.dispatch({ type: "APPLY_LAUNCH", tokenId: m.tokenId!, feeUsd: m.feeUsd ?? L.coinFeeUsd, devUsd: m.devUsd ?? 0, devTokens: m.devTokens ?? 0 });
      showToast(`🚀 $${cmd.ticker.toUpperCase()} is live in this world. Everyone can trade it.`);
      pushSnapshot(true);
    });
    return { ok: true };
  }
  if (["LAUNCH_AIRDROP", "ENTER_PRESALE", "VANO_SUBSCRIBE", "VANO_SNIPE"].includes(cmd.type)) return { ok: false, error: "Not available in shared worlds yet. Try it in single-player." };
  return null;
}

function onFill(m: Extract<ServerMsg, { t: "fill" }>) {
  if (!m.ok || !m.fill) return showToast(m.error ?? "Trade failed.");
  const f = m.fill;
  const t = engine!.state.market.tokens[f.tokenId];
  if (t) t.holders = f.remaining > 0 ? { player: f.remaining } : {};
  engine!.dispatch({ type: "APPLY_FILL", ...f });
  showToast(f.side === "buy" ? `Bought ${fmtTokens(f.tokens)} $${t?.ticker} for ${wusd(f.usd)}` : `Sold for ${wusd(f.usd)}`);
  pushSnapshot(true);
}

export async function startWorld(e: Engine, setHook: (h: typeof hook | null) => void) {
  engine = e;
  setHook(hook);
  await connect();
  setInterval(() => send({ t: "ping", at: Date.now() }), 20_000);
}

// ---------------------------------------------------------------- social
export async function postToWorld(text: string, groupId?: number, kind?: "post" | "alpha") {
  const m = await request<Extract<ServerMsg, { t: "posted" }>>({ t: "post", req: ++req, text, groupId, kind });
  if (!m.ok) throw new Error(m.error ?? "Couldn't post.");
}
export function report(postId: number | undefined, userId: string | undefined, reason: string) {
  send({ t: "report", postId, userId, reason });
}
export function setRelation(userId: string, kind: "mute" | "block", on: boolean) {
  send({ t: "relation", userId, kind, on });
  if (on) {
    muted.add(userId);
    worldPosts.value = worldPosts.value.filter((p) => p.userId !== userId);
  } else muted.delete(userId);
}
export async function groupOp(op: "create" | "join" | "leave", arg: { name?: string; groupId?: number }) {
  const m = await request<Extract<ServerMsg, { t: "groups" }>>({ t: "group", req: ++req, op, ...arg });
  if (m.error) throw new Error(m.error);
}
